import { JSX, useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { HotkeyHintsMenu } from '../../../components/menus/hotkey-hints-menu/HotkeyHintsMenu'
import { ModuleWindow } from '../../../components/moduleWindow/ModuleWindow'
import { translations } from '../../../configs/translations'
import { ClientApi } from '../../../services/clientApi'
import PlayerService from '../../../services/PlayerService'
import UserService from '../../../services/UserService'
import { useStore } from '../../../store/store'
import { NuiCallbacks, NuiMessages } from '../../../utils/ClientEvents'
import {
    createFreeroamHintsConfig,
    FreeroamHintActions,
} from './FreeroamControls.config'
import './FreeroamControls.css'

type FreeroamControlsProps = {
    onToggleHud?: () => void
}

export const FreeroamControls = ({
    onToggleHud,
}: FreeroamControlsProps) => {
    const language = useStore(state => state.language)
    const t = translations[language]

    const [moduleWindow, setModuleWindow] = useState<JSX.Element | null>(null)
    const [hideHintsAnimationStarted, setHideHintsAnimationStarted] = useState(false)
    const [areHintsShown, setAreHintsShown] = useState(true)

    const toggleCursor = useCallback(async (enabled: boolean) => {
        try {
            if (enabled) {
                await PlayerService.showCursor()
            } else {
                await PlayerService.hideCursor()
            }
        } catch (error) {
            console.log('Unable to toggle cursor, error:', error)
        }
    }, [])

    const handleLeave = useCallback(async () => {
        await UserService.leaveFreeroam().catch((error) => {
            toast.error(
                error?.response?.data && t[error.response.data as keyof typeof t]
                    ? t[error.response.data as keyof typeof t]
                    : t.error_unknown,
                { className: 'notification-error' }
            )
        })
    }, [t])

    const openLeaveWindow = useCallback(() => {
        void toggleCursor(true)

        setModuleWindow(
            <ModuleWindow
                title={t.leave}
                text={t.are_you_sure_leave_freeroam}
                onYes={() => {
                    void handleLeave()
                    setModuleWindow(null)
                }}
                onNo={() => {
                    setModuleWindow(null)
                    void toggleCursor(false)
                }}
            />
        )
    }, [t, handleLeave, toggleCursor])

    const toggleHints = useCallback(() => {
        const sound = new Audio(
            areHintsShown
                ? './sounds/woosh_1.wav'
                : './sounds/woosh_1_reversed.wav'
        )

        sound.volume = 0.01
        void sound.play().catch(() => undefined)

        if (areHintsShown) {
            setHideHintsAnimationStarted(true)

            window.setTimeout(() => {
                setAreHintsShown(false)
                setHideHintsAnimationStarted(false)
            }, 450)

            return
        }

        setAreHintsShown(true)
    }, [areHintsShown])

    const toggleDrift = useCallback(async () => {
        try {
            const result = await ClientApi.request<{ ok: boolean; reason?: 'no_vehicle' | 'too_fast' }>(
                NuiCallbacks.TOGGLE_DRIFT,
                {}
            )
            if (result.ok) {
                onToggleHud?.()
            } else if (result.reason === 'too_fast') {
                toast.error(t.please_stop_first)
            } else {
                toast.error(t.error_toggle_drift, { className: 'notification-error' })
            }
        } catch (error: any) {
            toast.error(
                error?.response?.data && t[error.response.data as keyof typeof t]
                    ? t[error.response.data as keyof typeof t]
                    : t.error_unknown,
                { className: 'notification-error' }
            )
        }
    }, [onToggleHud, t])

    const actions = useMemo<FreeroamHintActions>(() => ({
        leaveFreeroam: openLeaveWindow,
        toggleHints,
        toggleDriftHud: toggleDrift,
    }), [openLeaveWindow, toggleHints, toggleDrift])

    const hintsConfig = useMemo(
        () => createFreeroamHintsConfig(actions),
        [actions]
    )

    useEffect(() => {
        ClientApi.on(NuiMessages.FREEROAM_HINT, ({ id }: { id: string }) => {
            const matchedHint = hintsConfig.find(hint => hint.id === id)

            if (matchedHint) {
                void matchedHint.action()
            }
        })
    }, [hintsConfig])

    return (
        <>
            {moduleWindow}

            {areHintsShown && (
                <div className={`freeroam-hints-menu-wrapper ${hideHintsAnimationStarted ? 'slide-out' : ''}`}>
                    <HotkeyHintsMenu hints={hintsConfig.map(hint => ({
                        key: hint.id,
                        translation: t[hint.translationKey as keyof typeof t],
                        buttons: hint.buttons
                    }))} />
                </div>
            )}
        </>
    )
}